import React, { useEffect, useState, useCallback } from "react";
import Spacer from "../../Layout/Spacer";
import { Formik } from "formik";
import {
  FormControl,
  StyledField,
  FormActions,
  ErrorMessage,
  StyledSelect,
  Label,
  d,
} from "../../Form/FormControls";
import Button from "../../Button";
import ErrorMessages from "../../Notifications/ErrorMessages";
import Spinner from "../../Loaders/Spinner";
import { MdDelete } from "react-icons/md";
import {
  setAlert,
  updateAlertEnableFlag,
  updateAlert,
} from "../../../api/alertMutations";
import { useApi } from "../../../api/useApi";
import { filterDisabled } from "../../../common/helpers/util";
import SortTable from "../../Table/SortTable";
import Modal from "../../Modal";
import styled from "styled-components/macro";
import bcalogo from "../../../assets/images/BasecapIconWhite.png";
import * as Yup from "yup";
import { dataSourceAlerts } from "../../../api/dataSourceQueries";
import SplashLoader from "../../Loaders/SplashLoader";
import TableButton from "../../Button/TableButton";
import debounce from "lodash.debounce";

import { useSearch } from "../../../Hooks/useSearch";
import SearchSelect from "../../Forms/Search/SearchSelect";
import { usersInEnabledWorkGroups } from "../../../api/userQueries";
import { availableWorkGroups } from "../../../api/workgroupQueries";

const UserImage = styled.div`
border-radius: 50%;
height: 20px;
width: 20px;
border: 2px solid ${(props) => props.theme.menuText};
display: block;
background-image: url(${(props) =>
  props.profilePhoto
    ? "data:image/png;base64," + props.profilePhoto
    : bcalogo});
background-position: center;
background-repeat: no-repeat;
background-size: 20px};
margin-right: .5rem;
`;

const UserContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SmallUser = ({ user }) => {
  return (
    <UserContainer>
      <UserImage profilePhoto={user.profilePhoto} title={user.displayName} />
    </UserContainer>
  );
};

const AlertForm = ({ apiData, getUpdatedSource, showForm }) => {
  const alerts = apiData?.alerts;
  let isEditing = showForm?.id
    ? alerts.find((alert) => alert.id === showForm.id)
    : null;

  const criticalAlertExists = alerts.find(
    (a) => a.alertType === "DATA_QUALITY_CRITICAL_LEVEL"
  );
  const warningAlertExists = alerts.find(
    (a) => a.alertType === "DATA_QUALITY_WARNING_LEVEL"
  );
  const changeAlertExists = alerts.find(
    (a) => a.alertType === "DATA_QUALITY_CHANGE_DELTA"
  );
  const refreshSummaryAlertExists = alerts.find(
    (a) => a.alertType === "DATA_SOURCE_REFRESH_SUMMARY_REPORT"
  );
  const weeklySummaryAlertExists = alerts.find(
    (a) => a.alertType === "WEEKLY_DATA_SOURCE_SUMMARY"
  );
  const exportCustomAlertExists = alerts.find(
    (a) => a.alertType === "EXPORT_CUSTOM_REPORTS"
  );
  const recordLevelAlertExists = alerts.find(
    (a) => a.alertType === "DATA_QUALITY_RECORD_LEVEL"
  );
  const refreshExceptionAlertExists = alerts.find(
    (a) => a.alertType === "DATA_SOURCE_REFRESH_EXCEPTION"
  );

  const alertTypes = [
    {
      value: "DATA_QUALITY_CRITICAL_LEVEL",
      label: "Critical Level",
      disabled: criticalAlertExists && !isEditing,
    },
    {
      value: "DATA_QUALITY_WARNING_LEVEL",
      label: "Warning Level",
      disabled: warningAlertExists && !isEditing,
    },
    {
      value: "DATA_QUALITY_CHANGE_DELTA",
      label: "Change Delta",
      disabled: changeAlertExists && !isEditing,
    },
    {
      value: "DATA_SOURCE_REFRESH_SUMMARY_REPORT",
      label: "Refresh Summary",
      disabled: refreshSummaryAlertExists && !isEditing,
    },
    {
      value: "WEEKLY_DATA_SOURCE_SUMMARY",
      label: "Weekly Data Source Summary",
      disabled: weeklySummaryAlertExists && !isEditing,
    },
    {
      value: "EXPORT_CUSTOM_REPORTS",
      label: "Custom Reports Complete",
      disabled: exportCustomAlertExists && !isEditing,
    },
    {
      value: "DATA_QUALITY_RECORD_LEVEL",
      label: "Data Quality Record Level",
      disabled: recordLevelAlertExists && !isEditing,
    },
    {
      value: "DATA_SOURCE_REFRESH_EXCEPTION",
      label: "Data Source Refresh Exception",
      disabled: refreshExceptionAlertExists && !isEditing,
    },
  ];

  const [
    {
      loading: createAlertLoading,
      errors: createAlertErrors,
      data: createAlertData,
    },
    createAlert,
  ] = useApi();

  //callback
  const updateMeta = (values) => {
    let variables;
    const dataSourceId = (apiData && apiData.id) || null;
    if (isEditing) {
      variables = {
        alertId: isEditing.id,
        alertInfo: {
          sourceId: dataSourceId,
          sourceType: "DATA_SOURCE",
          type: values?.type?.value,
          threshold: values?.threshold,
          boolFlag: values?.boolFlag,
          instances: [
            {
              // hard coding to email-alerts only
              notificationType: "EMAIL",
              members: values.members.map((member) => {
                if (member.type === "group") {
                  return { workGroupId: member.value };
                } else {
                  return { userId: member.value };
                }
              }),
            },
          ],
        },
      };
      createAlert({ query: updateAlert, variables });
    } else {
      variables = {
        alert: {
          sourceId: dataSourceId,
          sourceType: "DATA_SOURCE",
          type: values?.type?.value,
          threshold: values?.threshold,
          boolFlag: values?.boolFlag,
          instances: [
            {
              // hard coding to email-alerts only
              notificationType: "EMAIL",
              members: values?.members.map((member) => {
                if (member.type === "group") {
                  return { workGroupId: member.value };
                } else {
                  return { userId: member.value };
                }
              }),
            },
          ],
        },
      };
      createAlert({ query: setAlert, variables });
    }
  };

  useEffect(() => {
    if (createAlertData && !createAlertErrors) {
      getUpdatedSource();
    }
  }, [createAlertData, createAlertErrors, getUpdatedSource]);

  const [stateUsers, setStateUsers] = useState();

  const [
    { errors: getUserErrors, loading: loadingUsers, data: usersData },
    getUsers,
  ] = useApi();

  const [
    { errors: getGroupsErrors, loading: loadingGroups, data: groupsData },
    getGroups,
  ] = useApi();

  const [userOptions, setUserOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);

  useEffect(() => {
    if (usersData?.usersInEnabledWorkGroups?.edges?.length) {
      const options = usersData.usersInEnabledWorkGroups?.edges?.map((ug) => {
        const user = ug?.node;
        return {
          label: user?.emailAddress,
          value: user?.id,
          type: "user",
        };
      });
      setUserOptions(options);
    } else {
      setUserOptions([]);
    }
  }, [usersData]);

  useEffect(() => {
    if (groupsData?.availableWorkGroups?.edges?.length) {
      const options = groupsData?.availableWorkGroups?.edges?.map((ug) => {
        const group = ug?.node;
        return {
          label: group?.displayName,
          value: group?.id,
          type: "group",
        };
      });
      setGroupOptions(options);
    } else {
      setGroupOptions([]);
    }
  }, [groupsData]);

  // Combine both results when either updates
  useEffect(() => {
    setStateUsers([...userOptions, ...groupOptions]);
  }, [userOptions, groupOptions]);

  const onSearch = useCallback(
    (text) => {
      if (!text) {
        setUserOptions([]);
        setGroupOptions([]);
        return;
      }

      getUsers({
        query: usersInEnabledWorkGroups,
        variables: {
          where: {
            or: [{ displayName: { contains: text } }],
          },
          order: [{ displayName: "ASC" }],
        },
      });

      getGroups({
        query: availableWorkGroups,
        variables: {
          where: {
            or: [{ displayName: { contains: text } }],
          },
          order: [{ displayName: "ASC" }],
        },
      });
    },
    [getUsers, getGroups]
  );

  const { inputText, handleInputChange, tooShortWarning } = useSearch({
    onSearch,
  });

  return (
    <>
      <Spacer />
      <Formik
        initialValues={{
          type: isEditing
            ? alertTypes.find((at) => at.value === isEditing.alertType)
            : null,
          // hard coded to first instance as currently only using email
          members: isEditing
            ? isEditing.instances[0]?.members.map((ug) => {
                if (ug?.user) {
                  return {
                    label: ug?.user?.emailAddress,
                    value: ug?.userId,
                    type: "user",
                  };
                } else {
                  return {
                    label: ug?.group?.displayName,
                    value: ug?.workGroupId,
                    type: "group",
                  };
                }
              })
            : null,
          threshold: isEditing ? isEditing.threshold : null,
          boolFlag: isEditing ? isEditing.boolFlag : null,
        }}
        enableReinitialize={true}
        validationSchema={Yup.object().shape({
          type: Yup.mixed().required("Required"),
          members: Yup.array().min(1).required("Required"),
          threshold: Yup.mixed().when("type", {
            is: (val) =>
              val != null &&
              val?.value !== "WEEKLY_DATA_SOURCE_SUMMARY" &&
              val?.value !== "DATA_SOURCE_REFRESH_SUMMARY_REPORT" &&
              val?.value !== "EXPORT_CUSTOM_REPORTS" &&
              val?.value !== "WEEKLY_DATA_SOURCE_SUMMARY" &&
              val?.value !== "DATA_SOURCE_REFRESH_EXCEPTION",
            then: Yup.mixed().required("Required"),
          }),
        })}
        validateOnMount={true}
        onSubmit={(values, actions) => {
          updateMeta(values);
        }}
      >
        {(props) => {
          return (
            <form onSubmit={props.handleSubmit}>
              <FormControl>
                <Label>Alert Type</Label>
                <StyledSelect
                  className={`react-select-container`}
                  classNamePrefix={`react-select`}
                  name={`type`}
                  id={`alertType`}
                  inputId={`alertType-input`}
                  instanceId={`alertType-instance`}
                  label="Alert Type"
                  options={alertTypes}
                  isOptionDisabled={(option) => option.disabled}
                  placeholder={`Alert Type`}
                  value={props.values.type}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  onChange={(e) => props.setFieldValue(`type`, e)}
                />
                <ErrorMessage name={`type`} />
              </FormControl>

              <FormControl>
                <Label>Members</Label>
                <SearchSelect
                  label="Search By Users and Groups"
                  placeholder="Search By Users and Groups"
                  options={stateUsers}
                  name={`members`}
                  id={`members`}
                  inputId={`members-input`}
                  instanceId={`members-instance`}
                  selectedOption={props.values?.members}
                  onChange={(e) => props.setFieldValue(`members`, e)}
                  inputValue={inputText}
                  onInputChange={handleInputChange}
                  loading={loadingUsers || loadingGroups}
                  error={getUserErrors || getGroupsErrors}
                  warning={tooShortWarning}
                  isMulti
                />

                {getUserErrors ? (
                  <ErrorMessages errors={getUserErrors} />
                ) : null}
                {getGroupsErrors ? (
                  <ErrorMessages errors={getGroupsErrors} />
                ) : null}

                <ErrorMessage name={`members`} />
                {getUserErrors ? (
                  <ErrorMessages errors={getUserErrors} />
                ) : null}
              </FormControl>

              {/* DATA_QUALITY_RECORD_LEVEL does not have a maximum threshold value of 100 */}
              {props?.values?.type?.value === "WEEKLY_DATA_SOURCE_SUMMARY" ||
              props?.values?.type?.value ===
                "DATA_SOURCE_REFRESH_SUMMARY_REPORT" ||
              props?.values?.type?.value === "EXPORT_CUSTOM_REPORTS" ||
              props?.values?.type?.value ===
                "DATA_SOURCE_REFRESH_EXCEPTION" ? null : (
                <FormControl>
                  <StyledField
                    type="number"
                    max={
                      props?.values?.type?.value === "DATA_QUALITY_RECORD_LEVEL"
                        ? null
                        : "100"
                    }
                    min="0"
                    name={`threshold`}
                    label="Threshold"
                    placeholder={`Value`}
                  />
                  <ErrorMessage name={`threshold`} />
                </FormControl>
              )}

              {props?.values?.type?.value === "WEEKLY_DATA_SOURCE_SUMMARY" ? (
                <FormControl>
                  <label>
                    Last Daily Report Only
                    <input
                      name={`boolFlag`}
                      type="checkbox"
                      checked={props.values.boolFlag}
                      onChange={() => {
                        props.setFieldValue(`boolFlag`, !props.values.boolFlag);
                      }}
                    />
                  </label>
                </FormControl>
              ) : null}

              {createAlertErrors ? (
                <ErrorMessages errors={createAlertErrors} />
              ) : null}

              <FormActions>
                <Button
                  type="submit"
                  disabled={createAlertLoading || !props.isValid}
                >
                  {createAlertLoading ? (
                    <Spinner />
                  ) : isEditing ? (
                    "Edit Alert"
                  ) : (
                    "Add Alert"
                  )}
                </Button>
              </FormActions>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

const AlertTable = ({ apiData, setShowForm, getUpdatedSource }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState(null);

  useEffect(() => {
    if (alertToDelete) {
      setShowConfirm(true);
    } else {
      setShowConfirm(false);
    }
  }, [alertToDelete]);

  const [
    { loading: removeAlertDataLoading, data: removeAlertData },
    removeAlertApi,
  ] = useApi();

  //callback for removing a row
  const removeAlert = useCallback(
    (alert) => {
      const variables = {
        alertId: alert?.id,
        type: alert?.type,
        enabled: false,
      };
      removeAlertApi({ query: updateAlertEnableFlag, variables });
    },
    [removeAlertApi]
  );

  const [alerts, setAlerts] = useState(filterDisabled(apiData?.alerts ?? []));

  //handle remove rule update
  useEffect(() => {
    if (removeAlertData) {
      //set filtered rule
      setAlerts((prevAlerts) =>
        prevAlerts.filter(
          (alert) => alert.id !== removeAlertData?.updateAlertEnableFlag.id
        )
      );
      setAlertToDelete(null);
      getUpdatedSource();
    }
  }, [removeAlertData, getUpdatedSource]);

  //set rules into local state so we can mutate on remove
  useEffect(() => {
    if (apiData?.alerts) {
      setAlerts(filterDisabled(apiData?.alerts ?? []));
    }
  }, [apiData]);

  const columnsData = [
    {
      Header: "Type",
      id: "type",
      width: 120,
      accessor: (d) => d.alertType,
    },
    {
      Header: "Threshold",
      id: "threshold",
      width: 120,
      accessor: (d) => d.threshold,
    },
    {
      Header: "Members",
      id: "Members",
      Cell: ({ row: { original } }) => {
        const members = original?.instances[0]?.members ?? [];
        return (
          <React.Fragment key={`alert-members-${original.id}`}>
            {members.map((member, i) => {
              const isGroup = member?.workGroupId;
              return (
                <React.Fragment key={i}>
                  {isGroup ? (
                    member?.group?.displayName
                  ) : (
                    <>
                      <SmallUser user={member.user} />
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      },
    },
    {
      Header: " ",
      id: "actions",
      width: 220,
      sortable: false,
      Cell: ({ row: { original } }) => {
        return (
          <React.Fragment key={`alert-actions-${original.id}`}>
            <TableButton
              list="true"
              onClick={() => setShowForm({ id: original.id })}
              type="button"
            >
              Edit
            </TableButton>
            <TableButton
              list="true"
              danger
              onClick={() => setAlertToDelete(original)}
              type="button"
            >
              <MdDelete />
            </TableButton>
          </React.Fragment>
        );
      },
    },
  ];

  return (
    <>
      {showConfirm ? (
        <Modal
          title={`Confirm Alert Removal`}
          hide={() => setAlertToDelete(null)}
        >
          <p>Are you sure you wish to remove this Alert?</p>
          <div>
            <Button
              type="button"
              list="true"
              disabled={removeAlertDataLoading}
              danger
              onClick={() => removeAlert(alertToDelete)}
            >
              {removeAlertDataLoading ? <Spinner /> : "Yes"}
            </Button>
            <Button
              type="button"
              disabled={removeAlertDataLoading}
              onClick={() => {
                setAlertToDelete(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal>
      ) : null}

      <SortTable data={alerts} columns={columnsData} />
    </>
  );
};

const Alerts = ({ sourceId }) => {
  const [{ loading, errors, data }, getSource] = useApi(dataSourceAlerts, {
    id: Number(sourceId),
  });
  const apiData = data?.dataSource;

  const getUpdatedSource = useCallback(() => {
    getSource({
      query: dataSourceAlerts,
      variables: { id: Number(sourceId) },
    });
    setShowForm(false);
  }, [sourceId, getSource]);

  const [showForm, setShowForm] = useState(false);

  if (loading) return <SplashLoader text={"Loading Alerts"} />;
  if (errors) return <ErrorMessages errors={errors} />;

  return (
    <>
      {showForm ? (
        <>
          <div>
            <Button danger onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
          <AlertForm
            apiData={apiData}
            getUpdatedSource={getUpdatedSource}
            showForm={showForm}
          />
        </>
      ) : (
        <>
          <div>
            <Button onClick={() => setShowForm((prevState) => !prevState)}>
              Create Alert
            </Button>
          </div>
          <AlertTable
            apiData={apiData}
            setShowForm={setShowForm}
            getUpdatedSource={getUpdatedSource}
          />
        </>
      )}
    </>
  );
};

export default Alerts;
